'use client';

import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

import { type MessageInputProps } from '../types';

export type MessageInputHandle = {
  focus: () => void;
}

export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(function MessageInput({
  onSend,
  onFileUpload,
  disabled = false,
  placeholder = 'Type your message...',
  maxLength = 1000,
  allowFileUpload = false,
  acceptedFileTypes = ['.txt', '.pdf', '.docx', '.md', '.json']
}: MessageInputProps, ref) {
  const [input, setInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const safeOnSend = typeof onSend === 'function' ? onSend : undefined;
  const safeOnFileUpload = typeof onFileUpload === 'function' ? onFileUpload : undefined;
  const safeAcceptedTypes = Array.isArray(acceptedFileTypes)
    ? acceptedFileTypes.filter((t): t is string => typeof t === 'string')
    : [];

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  }), []);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (input.trim() && !disabled && safeOnSend) {
      safeOnSend(input.trim());
      setInput('');
    }
  }, [input, disabled, safeOnSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !disabled && safeOnSend) {
        safeOnSend(input.trim());
        setInput('');
      }
    }
  }, [input, disabled, safeOnSend]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0 && safeOnFileUpload) {
      const fileArray = Array.from(files);
      safeOnFileUpload(fileArray);
    }
  }, [safeOnFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (allowFileUpload) {
      setIsDragOver(true);
    }
  }, [allowFileUpload]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (allowFileUpload) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [allowFileUpload, handleFileSelect]);

  const handleFileInputClick = useCallback(() => {
    if (allowFileUpload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [allowFileUpload]);

  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid var(--border-default)',
      backgroundColor: 'var(--background-secondary)'
    }}>
      <form onSubmit={handleSubmit}>
        <div 
          style={{ 
            display: 'flex', 
            gap: '8px',
            position: 'relative'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div style={{ flex: 1, position: 'relative' }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${isDragOver ? 'var(--primary-default)' : 'var(--input-border)'}`,
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'var(--input-background)',
                color: 'var(--input-foreground)',
                resize: 'vertical',
                minHeight: '40px',
                maxHeight: '120px',
                fontFamily: 'inherit',
                lineHeight: '1.5'
              }}
            />
            
            {/* Drag overlay */}
            {isDragOver && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'var(--chat-accent-muted)',
                border: '2px dashed var(--chat-accent)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                color: 'var(--chat-accent)',
                fontWeight: '500'
              }}>
                Drop files here
              </div>
            )}
          </div>

          {/* File upload button */}
          {allowFileUpload && (
            <button
              type="button"
              onClick={handleFileInputClick}
              disabled={disabled}
              aria-label="Upload file"
              style={{
                padding: '8px 12px',
                backgroundColor: disabled ? 'var(--border-default)' : 'var(--background-muted)',
                color: disabled ? 'var(--foreground-disabled)' : 'var(--foreground-muted)',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              📎
            </button>
          )}

          {/* Send button */}
          <button
            type="submit"
            disabled={disabled || !input.trim()}
            style={{
              padding: '8px 12px',
              backgroundColor: disabled || !input.trim() ? 'var(--border-default)' : 'var(--chat-accent)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: disabled || !input.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: '500'
            }}
          >
            Send
          </button>
        </div>

        {/* Hidden file input */}
        {allowFileUpload && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={safeAcceptedTypes.length > 0 ? safeAcceptedTypes.join(',') : undefined}
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: 'none' }}
          />
        )}
      </form>

      {/* Character count */}
      {maxLength && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '8px',
          fontSize: '11px',
          color: 'var(--foreground-subtle)'
        }}>
          <span>
            Press Enter to send, Shift+Enter for new line
          </span>
          <span style={{
            color: input.length > maxLength * 0.9 ? 'var(--danger-default)' : 'var(--foreground-subtle)'
          }}>
            {input.length}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
});
